/**
 * useFuelBalance — the SINGLE canonical source for the user's fuel / credit
 * balance across the entire app.
 *
 * There used to be two disagreeing sources for "credits / fuel":
 *   1. `user.fuel_credits` (USD) on the auth store — used by billing, banners,
 *      payment-result, developer-portal, admin tables.
 *   2. `capStatus.credits_remaining` from the live `CAP_STATUS_UPDATE` SSE
 *      stream — used by the in-chat UsageBar.
 *
 * They could drift because the SSE number never flowed back into the auth
 * store. This module collapses them into ONE source:
 *
 *   - `useFuelBalance()` returns the canonical balance from the auth store
 *     (`user.fuel_credits`), already coerced to a number, plus the unified
 *     `formatFuel()` rendering.
 *   - `useCapStatusFuelSync()` subscribes to the `CAP_STATUS_UPDATE` events on
 *     the event bus and writes `credits_remaining` back into the auth store's
 *     `fuel_credits`, so the live number flows through the SAME source. Mount it
 *     once near the app root (see App.tsx).
 *
 * Every credit/fuel display should read `useFuelBalance()` (or `formatFuel`)
 * so the value is singular and formatted identically everywhere.
 */
import { useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useEventBus } from '../stores/eventBus';
import { formatFuel } from '../utils/formatting';

export interface FuelBalance {
  /** Canonical balance in USD (NOT cents). Always a finite number. */
  credits: number;
  /** The same value rendered with the unified `formatFuel` formatter. */
  formatted: string;
  /** True when there is no authenticated user to read a balance from. */
  isUnknown: boolean;
}

/**
 * Read the canonical fuel balance + its unified formatting.
 *
 * Subscribes to the auth store so any change (refreshUser, login, or the live
 * `CAP_STATUS_UPDATE` sync) re-renders consumers.
 */
export function useFuelBalance(): FuelBalance {
  const rawCredits = useAuthStore((s) => (s.user ? s.user.fuel_credits : null));
  const credits = Number(rawCredits ?? 0) || 0;
  return {
    credits,
    formatted: formatFuel(credits),
    isUnknown: rawCredits == null,
  };
}

/**
 * Mount-once hook: keep `user.fuel_credits` in sync with the live
 * `CAP_STATUS_UPDATE` SSE stream so the live number and the stored number are
 * one and the same. Place this in a component that is always mounted while a
 * session is active (App root).
 */
export function useCapStatusFuelSync(): void {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const setFuelCredits = useAuthStore((s) => s.setFuelCredits);

  useEffect(() => {
    if (!userId) return;
    return useEventBus.subscribe((next, previous) => {
      const previousLastId = previous.events[previous.events.length - 1]?.id;
      let newEvents = next.events;
      if (previousLastId) {
        const previousLastIndex = next.events.findIndex((event) => event.id === previousLastId);
        newEvents = previousLastIndex >= 0 ? next.events.slice(previousLastIndex + 1) : [];
      }

      for (const event of newEvents) {
        if (event.type !== 'CAP_STATUS_UPDATE' || !event.payload || typeof event.payload !== 'object') continue;
        const update = event.payload as Record<string, unknown>;
        if (update.ownerUserId !== userId) continue;
        if (!update.capStatus || typeof update.capStatus !== 'object') continue;
        const cap = update.capStatus as Record<string, unknown>;
        if (cap.tier === 'guest') continue;
        const credits = cap.credits_remaining;
        if (typeof credits === 'number' && Number.isFinite(credits)) {
          setFuelCredits(credits);
        }
      }
    });
  }, [userId, setFuelCredits]);
}
